/* eslint-disable import/no-restricted-paths */
import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { ICoordinatesSchema } from '../../repositories/special-promos/entities/entities/CoordinatesSchema';

import { IMap, IPlacemark, IPlacemarkOptions, IYmaps, YmapsCoords } from '../../../browser/ymaps/types';
import { YmapsApi } from '../../../browser/ymaps/ymaps-api';
import { JkPreview } from './jk-preview';
import { getBalloonLayout, getIconLayout } from './layouts';
import { OfficePreview } from './office-preview';
import { ZoomControls } from './zoom';

import * as styles from './index.css';
import classNames from 'classnames';

const MAX_ZOOM = 16;
const DEFAULT_ZOOM = 9;
const DEFAULT_MAP_CENTER: YmapsCoords = [55.755814, 37.617635];
const MAP_MODULES = [
  'Map',
  'Placemark',
  'geoObject.addon.balloon',
  'templateLayoutFactory',
  'geometry.pixel.Rectangle',
  'shape.Rectangle',
];

export interface IJkMapData {
  address: string;
  url: string;
  imageUrl: string;
  name: string;
  buildingStatusInfo: string;
  minPrice: number;
  coordinates: ICoordinatesSchema;
}

export interface IOfficeMapData {
  address: string;
  isMain: boolean;
  coordinates: ICoordinatesSchema;
  name: string;
  phone: string;
}

type MapObject = IJkMapData | IOfficeMapData;

const isJkMapData = (data: MapObject): data is IJkMapData => 'imageUrl' in data && 'buildingStatusInfo' in data;

const formatZoom = (zoom?: number) => {
  if (!zoom) {
    return DEFAULT_ZOOM;
  }

  return zoom > MAX_ZOOM ? MAX_ZOOM : zoom;
};

interface IMapProps {
  mapId: string;
  pins: MapObject[];
  initialCenter?: YmapsCoords;
  initialZoom?: number;
  ymaps?: YmapsApi;
  mainColor: string;
  pinImageUrl?: string;
  mapClassName?: string;
}

interface IMapState {
  mapIsLoaded: boolean;
}

class MapContainer {
  private id: string;
  private ymaps: IYmaps;
  private balloonLayout: Function;
  private iconLayout: Function;
  private placemarks: IPlacemark[] = [];
  public map: IMap;
  public mainColor: string;
  public pinImageUrl?: string;

  public constructor(
    ymaps: IYmaps,
    id: string,
    mainColor: string,
    pinImageUrl?: string,
    center?: YmapsCoords,
    zoom?: number,
  ) {
    this.ymaps = ymaps;
    this.id = id;
    this.mainColor = mainColor;
    this.pinImageUrl = pinImageUrl;
    this.map = new ymaps.Map(id, {
      center: center || DEFAULT_MAP_CENTER,
      controls: [],
      maxZoom: MAX_ZOOM,
      zoom: formatZoom(zoom),
    });
  }

  public createPlacemark = (mapObject: MapObject) => {
    const { coordinates } = mapObject;

    if (!coordinates || coordinates.lng === undefined || coordinates.lat === undefined) {
      return;
    }

    const preview = isJkMapData(mapObject) ? <JkPreview {...mapObject} /> : <OfficePreview {...mapObject} />;

    const placemark = new this.ymaps.Placemark(
      [coordinates.lat ?? 0, coordinates.lng ?? 0],
      { balloonContent: renderToStaticMarkup(preview) },
      this.getPlacemarkOptions(isJkMapData(mapObject)),
    );

    this.placemarks.push(placemark);
    this.map.geoObjects.add(placemark);
  };

  public createPlacemarks = (mapObjects: MapObject[]) => {
    mapObjects.forEach(this.createPlacemark);
  };

  public disable = (behavior: string): void => {
    this.map.behaviors.disable(behavior);
  };

  public openBalloon = (index: number): void => {
    if (this.placemarks[index]) {
      this.placemarks[index].balloon.open();
    }
  };

  private getPlacemarkOptions(isJkMap: boolean): IPlacemarkOptions {
    if (!this.balloonLayout) {
      this.balloonLayout = getBalloonLayout(this.ymaps, isJkMap, this.id);
    }
    if (!this.iconLayout) {
      this.iconLayout = getIconLayout(this.ymaps, this.mainColor, this.pinImageUrl);
    }

    return {
      balloonLayout: this.balloonLayout,
      hideIconOnBalloonOpen: false,
      iconImageSize: [46, 55],
      iconLayout: this.iconLayout,
      iconOffset: [-23, -55],
      iconShape: {
        coordinates: [33, 23],
        radius: 23,
        type: 'Circle',
      },
    };
  }
}

export class Map extends React.PureComponent<IMapProps, IMapState> {
  private mapContainer: MapContainer;

  public constructor(props: IMapProps) {
    super(props);

    this.state = { mapIsLoaded: false };
  }

  public async componentDidMount() {
    if (this.props.ymaps) {
      const { mainColor, pins, mapId, initialCenter, initialZoom, pinImageUrl } = this.props;

      const ymaps = await this.props.ymaps.loadYmaps(MAP_MODULES);

      this.mapContainer = new MapContainer(ymaps, mapId, mainColor, pinImageUrl, initialCenter, initialZoom);
      this.mapContainer.disable('scrollZoom');
      this.mapContainer.createPlacemarks(pins);
      this.setState({ mapIsLoaded: true });

      if (pins.length && !isJkMapData(pins[0])) {
        let needOpenBalloonIndx = 0;

        (pins as IOfficeMapData[]).forEach((pin, index) => {
          if (pin.isMain) {
            needOpenBalloonIndx = index;
          }
        });

        this.mapContainer.openBalloon(needOpenBalloonIndx);
      }
    }
  }

  public render() {
    return (
      <div className={styles['container']}>
        <div id={this.props.mapId} className={classNames(styles['map'], this.props.mapClassName)} />
        {this.state.mapIsLoaded && <ZoomControls onZoomIn={this.onZoomIn} onZoomOut={this.onZoomOut} />}
      </div>
    );
  }

  private onZoomIn = () => {
    const { map } = this.mapContainer;
    map.setZoom(map.getZoom() + 1);
  };

  private onZoomOut = () => {
    const { map } = this.mapContainer;
    map.setZoom(map.getZoom() - 1);
  };
}
